import { useHireJob } from '@features/buyer/hire/hire-job-context';
import type { HireStepNavigation } from '@features/buyer/hire/hire-r8tr/hireR8trTypes';
import {
  CategoryPickers,
  isAutomotiveCategory,
  JobPriceFields,
} from '@features/buyer/hire/job-price-calculator';
import TermConditionDialog from '@features/buyer/hire/term-condition-dialog';
import { useCategoriesQuery } from '@lib/api/categories';
import { type ChangeEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { Category } from './jobPriceCalculatorTypes';
import { useBuyerCostEstimate } from './useBuyerCostEstimate';
import { useJobPriceCategorySelection } from './useJobPriceCategorySelection';

interface JobPriceCalculatorProps extends HireStepNavigation {
  onPressNext: () => void;
  handleTosterFire: (message: string) => void;
}

const JobPriceCalculator = ({
  onPressNext,
  handleTosterFire,
  onBack,
  onCancel,
}: JobPriceCalculatorProps): JSX.Element => {
  const { t } = useTranslation();
  const { createJob, saveJob } = useHireJob();
  const { data: categoryData } = useCategoriesQuery();
  const allCategories = (categoryData ?? []) as Category[];
  const [askingPrice, setAskingPrice] = useState('');
  const [termsOpen, setTermsOpen] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);

  const categorySelection = useJobPriceCategorySelection({
    allCategories,
    createJob,
    saveJob,
    setAskingPrice,
    setTermsAccepted,
  });

  const { buyerCostState, buyerCostError, isBuyerCostLoading } =
    useBuyerCostEstimate({
      categoryId: categorySelection.selectedCategory?.id,
      askingPrice,
    });

  const canSubmit =
    termsAccepted &&
    categorySelection.selectedCategoryType != null &&
    categorySelection.selectedCategory != null &&
    categorySelection.selectedSubCategory != null &&
    askingPrice !== '' &&
    Number(askingPrice) >= 0 &&
    buyerCostState != null &&
    !buyerCostError &&
    !isBuyerCostLoading;

  const handleAskingPrice = (event: ChangeEvent<HTMLInputElement>): void => {
    setAskingPrice(event.target.value);
  };

  const handleSubmit = (): void => {
    const category = categorySelection.selectedCategory;
    const subCategory = categorySelection.selectedSubCategory;
    if (!canSubmit || !category || !subCategory || buyerCostState == null) {
      if (isBuyerCostLoading) {
        handleTosterFire(t('hire.waitForFee'));
        return;
      }
      if (buyerCostError) {
        handleTosterFire(buyerCostError);
        return;
      }
      handleTosterFire(t('hire.fillRequired'));
      return;
    }
    saveJob({
      categoryId: category.id,
      subCategoryId: subCategory.id,
      buyerCost: String(buyerCostState),
      discountedBuyerCost: undefined,
      askingPrice: String(Number(askingPrice)),
      termsAccepted,
      isAutomotive: isAutomotiveCategory(category),
      lineOfBusiness: category.lineOfBusiness || null,
    });
    onPressNext();
  };

  return (
    <>
      <JobPriceFields
        selection={{
          categoryType: categorySelection.selectedCategoryType,
          category: categorySelection.selectedCategory,
          subCategory: categorySelection.selectedSubCategory,
          openTypePicker: categorySelection.pickers.openType,
          openCategoryPicker: categorySelection.pickers.openCategory,
          openSubCategoryPicker: categorySelection.pickers.openSubCategory,
        }}
        pricing={{
          askingPrice,
          onAskingPriceChange: handleAskingPrice,
          buyerCost: buyerCostState,
          buyerCostError,
          isBuyerCostLoading,
        }}
        terms={{
          accepted: termsAccepted,
          onAcceptedChange: setTermsAccepted,
          onOpenTerms: () => setTermsOpen(true),
        }}
        canSubmit={canSubmit}
        onSubmit={handleSubmit}
        onBack={onBack}
        onCancel={onCancel}
      />
      <CategoryPickers
        open={{
          type: categorySelection.pickers.typeOpen,
          category: categorySelection.pickers.categoryOpen,
          subCategory: categorySelection.pickers.subCategoryOpen,
        }}
        onOpenChange={{
          type: categorySelection.pickers.setTypeOpen,
          category: categorySelection.pickers.setCategoryOpen,
          subCategory: categorySelection.pickers.setSubCategoryOpen,
        }}
        selectedCategoryType={categorySelection.selectedCategoryType}
        categories={categorySelection.filteredCategories}
        selectedCategory={categorySelection.selectedCategory}
        onSelectType={categorySelection.handleSelectedCategoryType}
        onSelectCategory={categorySelection.handleSelectedCategory}
        onSelectSubCategory={categorySelection.handleSelectedSubCategory}
      />

      {termsOpen ? (
        <TermConditionDialog handleDialogBox={() => setTermsOpen(false)} />
      ) : null}
    </>
  );
};

export default JobPriceCalculator;
